/**
 * AbilityTelemetryTab — engine render metrics and deep diagnostics links.
 *
 * Modernized metric cards, projectile receipt badge, and diagnostic window buttons.
 */
import React from 'react';
import { Button, Card, Badge } from '../../primitives';
import {
  COLORS,
  FONTS,
  FONT_SIZE,
  FONT_WEIGHT,
  PLAYER_UI,
  RADII,
  SPACE,
} from '../../theme';
import { windowBridge } from '../../framework/windowBridge';
import { type H2kProjectileRow, type H2kSnapshot } from './types';

interface AbilityTelemetryTabProps {
  snap: H2kSnapshot | null;
  lastProjectile: H2kProjectileRow | null;
  localProjectilesCount: number;
}

export const AbilityTelemetryTab: React.FC<AbilityTelemetryTabProps> = ({
  snap,
  lastProjectile,
  localProjectilesCount,
}) => {
  return (
    <div style={styles.container}>
      <Card
        title="Render Truth & Telemetry"
        subtitle="Engine spawn events pin even single-frame projectiles"
        icon="◈"
      >
        <div style={styles.metricsGrid}>
          <Metric label="Live Shots" value={localProjectilesCount} />
          <Metric label="Active Effects" value={snap?.effects?.length ?? 0} />
          <Metric label="Cast Bars" value={snap?.castBars?.length ?? 0} />
        </div>

        {lastProjectile ? (
          <div style={styles.projectileReceipt} data-testid="grounds-last-projectile">
            <Badge tone="success" size="sm">LAST SHOT</Badge>
            <strong style={styles.projId}>{lastProjectile.abilityId ?? 'unknown'}</strong>
            <span style={styles.projSize}>{lastProjectile.width}×{lastProjectile.height}px</span>
            <span style={styles.projShape}>{lastProjectile.shape ?? 'default'}</span>
          </div>
        ) : (
          <div style={styles.emptyReceipt}>Cast a projectile to pin its render receipt here.</div>
        )}
      </Card>

      <Card
        title="Deep Diagnostic Probes"
        subtitle="Opt-in telemetry windows for frame-by-frame analysis"
        icon="🔍"
      >
        <div style={styles.diagnosticsGrid}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => windowBridge.toggle('targetAbilityCheck')}
          >
            Range & Line of Sight Gates →
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => windowBridge.toggle('abilityLifecycle')}
          >
            Cast Timeline Inspector →
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => windowBridge.toggle('visualLifecycle')}
          >
            Visual Stream Telemetry →
          </Button>
        </div>
      </Card>
    </div>
  );
};

const Metric: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div style={styles.metric}>
    <span style={styles.metricValue}>{value}</span>
    <span style={styles.metricLabel}>{label}</span>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.sm,
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: SPACE.xs,
    marginBottom: SPACE.xs,
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: `${SPACE.sm}px ${SPACE.xs}px`,
    borderRadius: RADII.sm,
    background: 'rgba(0, 0, 0, 0.35)',
    border: `1px solid ${COLORS.borderSubtle}`,
    gap: 2,
  },
  metricValue: {
    color: PLAYER_UI.brassBright,
    fontSize: FONT_SIZE.xl,
    fontWeight: FONT_WEIGHT.bold,
    fontFamily: FONTS.mono,
  },
  metricLabel: {
    color: COLORS.textMuted,
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  projectileReceipt: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.sm,
    padding: `${SPACE.xs}px ${SPACE.sm}px`,
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    background: 'rgba(0, 0, 0, 0.25)',
    fontFamily: FONTS.mono,
    fontSize: FONT_SIZE.xs,
  },
  projId: {
    color: COLORS.textPrimary,
  },
  projSize: {
    color: PLAYER_UI.brassBright,
  },
  projShape: {
    color: COLORS.textMuted,
    marginLeft: 'auto',
  },
  emptyReceipt: {
    color: COLORS.textMuted,
    fontSize: FONT_SIZE.xs,
    fontStyle: 'italic',
    textAlign: 'center',
    padding: SPACE.sm,
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: RADII.sm,
  },
  diagnosticsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
  },
};
